<script lang="ts">
	//Svelte hooks
	import { createEventDispatcher, onMount } from 'svelte';
	//Design system components
	import {
		Text,
		Alert,
		CardGrid,
		SelectCard,
		Slider,
		Container,
		Toggle,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from '../../../../../../../../../helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import { readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	//Radio favorites
	import { checkRadioFavorites } from 'src/components/Scenes/helpers/radio_favorites_cache';
	//Types
	import type { MediaResponseInterface } from 'src/types/MediaResponseInterface';
	//Components
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let actions = [];
	export let active_actions = []; // because it is only one action we can be sure that active_actions contains only one element
	export let deviceId;
	export let sceneIsValid; // Do not remove this line, it is used in the parent component and its validation

	const device = $devices[real_id(deviceId)]; //Todo remove deviceID from actions!!!
	const action = actions[0]; //getting the first action and using it as a reference and default value
	const dispatch = createEventDispatcher();

	let values = actions[0]?.values || [];
	let mediaFiles = []; //array of media files received from the device
	let showSongList = false;
	let hasRadioFavs = false;
	let checkingRadioFavorites = false;

	let selectedAction = active_actions[0]?.name || values?.[0]; //getting the selected action from the active actions or from the first action in the list

	let enableVolumeControl = typeof active_actions[0]?.value?.volume !== 'undefined' ? true : false; //this is used to disable the volume control
	let volume = enableVolumeControl
		? active_actions[0]?.value?.volume ||
			readDeviceStatus(real_id(deviceId))?.media?.playback?.volume
		: 0; //getting the volume from the interface status if it is not set in the action
	/**
	 * Updates the media files list.
	 * Sets the name and image for each media file and marks the selected file.
	 *
	 * @param {string | undefined} id - The ID of the media file to select. If undefined, selects the media file based on the active action.
	 */
	const updateMediaFiles = (id = undefined) => {
		mediaFiles = mediaFiles.map((m) => ({
			...m,
			name: capFirstChar(m.title),
			image: m.preview,
			selected: typeof id === 'undefined' ? active_actions[0]?.value?.id === m.id : m.id === id, //adding the selected property to the media file and image and name for the select list
		}));
	};

	/**
	 * Loads media files from the device.
	 * Filters the media files to include only audio files and updates the media files list.
	 *
	 * @async
	 * @returns {Promise<void>}
	 */
	const loadMediaFiles = async () => {
		const res: MediaResponseInterface = await gen2Request(device, 'Media.List', {});
		if (res?.isok) {
			const list = res.data?.list || res.list;

			mediaFiles = list?.filter((i) => i.type === 'AUDIO') || [];
			updateMediaFiles();
		}
	};

	/**
	 * Prepares the properties for the given action ID.
	 * Returns a formatted string based on the action ID.
	 *
	 * @param {string} id - The action ID.
	 * @param {any} _val - The value associated with the action ID.
	 * @returns {object} - The formatted properties for the action.
	 */
	const _prepare_props_by_id = (id, _val) => {
		//Keys below are the original action names. At the moment we are using only Play media from radio.
		/**
		 * 'Radio_PlayFavourite',
		 * 'Radio_Pause',
		 * 'Radio_Stop',
		 * 'MP_Play',
		 * 'MP_Play_Audio_Clip',
		 * 'MP_Pause',
		 * 'MP_Stop',
		 */

		switch (id) {
			case 'Radio_PlayFavourite':
				return {
					title: $format('scene_action_radio_play_favourite', {
						default: 'Play media from radio',
					}),
					icon: 'fas fa-play text-success',
				};
			// case 'Radio_Pause': //this is not implemented
			// 	return $format('scene_action_radio_pause', {
			// 		default: 'Pause the radio.',
			// 	});
			// case 'Radio_Stop'://This is not needed because media stop will stop both media and radio
			// 	return $format('scene_action_radio_stop', {
			// 		default: 'Stop the radio',
			// 	});
			case 'MP_Play':
				return {
					title: $format('scene_action_mp_play', {
						default: 'Select song to play',
					}),
					icon: 'fas fa-eject text-success',
				};
			case 'MP_Pause':
				return {
					title: $format('scene_action_mp_pause', {
						default: 'Pause media player',
					}),
					icon: 'fas fa-pause',
				};
			case 'MP_Stop':
				return {
					title: $format('scene_action_mp_stop', {
						default: 'Stop media',
					}),
					icon: 'fal fa-stop text-danger',
				};
			default:
				return {};
		}
	};

	$: deviceStateList = values.map((action) => ({
		id: action,
		title: _prepare_props_by_id(action, action).title,
		icon: _prepare_props_by_id(action, action).icon,
		selected: action === selectedAction,
	})); /**this is the list of actions to be gathered like:
	 * [
	 *  {
	 *      "id": "Radio_PlayFavourite",
	 *      "title": "Възпроизвеждане на медия от радиото",
	 *      "icon": "fas fa-play text-success",
	 *      "selected": true
	 *  },
	 * {
	 *     "id": "MP_Play",
	 *     "title": "Изберете песен за възпроизвеждане",
	 *     "icon": "fas fa-eject text-success",
	 *     "selected": false
	 * },
	 *  {
	 *      "id": "MP_Pause",
	 *      "title": "Поставете медийния плейър на пауза",
	 *      "icon": "fas fa-pause",
	 *      "selected": false
	 *  },
	 *  {
	 *      "id": "MP_Stop",
	 *      "title": "Спрете медийния плейър",
	 *      "icon": "fal fa-stop text-danger",
	 *      "selected": false
	 *  }
	 * ]
	 *
	 */

	/**
	 * Updates the active action with the provided action details and dispatches the change event.
	 *
	 * @param {object} _action - The action object containing the name and value.
	 */
	const updateActiveAction = (_action) => {
		const _volume = enableVolumeControl ? volume : undefined;
		action['name'] = _action.name;
		action['value'] = {
			id: _action?.value?.id || 0,
			volume: _volume,
		};
		dispatch('change_active', { new_active: [...[action]] });
	};
	/**
	 * Handles the device action change event.
	 * Updates the active actions with the selected action and dispatches the change event.
	 * If the action is 'MP_Play', it shows the modal and selects a default song to play.
	 *
	 * @param {string} action - The selected action.
	 */
	const onChangeDeviceAction = async (action) => {
		selectedAction = action;
		if (action === 'Radio_PlayFavourite') {
			await checkRadioFavs();
		}
		if (action === 'MP_Play' && !showSongList && mediaFiles.length > 0) {
			showSongList = true;
			selectSong({ detail: { id: Object.values(mediaFiles)[0]?.id } }); //we need a default song to play
			return;
		}
		if (action !== 'MP_Play') showSongList = false;
		updateActiveAction({
			name: action,
			value: {
				id: 0,
				volume: enableVolumeControl ? volume : undefined,
			},
		});
	};

	/**
	 * Handles the volume change event.
	 * Updates the volume in the active actions and dispatches the change event.
	 *
	 * @param {Event} e - The event object containing the new volume value.
	 */
	const onChangeVolume = (e) => {
		const action = deviceStateList.filter((d) => d.selected)?.[0]?.id || active_actions[0].name;
		volume = e.detail.value;
		updateActiveAction({
			name: action,
			value: { id: active_actions?.[0]?.value?.id || 0, volume },
		});
	};

	/**
	 * Selects a song from the media files list and updates the component's state.
	 * @param {object} e - event object with the id of the selected song.
	 * @returns {void}
	 */
	const selectSong = (e) => {
		const action = active_actions[0].name;
		const cacheVolume = enableVolumeControl ? volume : undefined;
		active_actions[0] = {
			name: action,
			value: { id: e.detail.id, volume: cacheVolume },
		};
		updateMediaFiles(e.detail.id);
		updateActiveAction({
			name: action,
			value: { id: e.detail.id, volume: cacheVolume },
		});
	};

	/**
	 * Checks if the device has radio favorites and updates the state.
	 *
	 * @async
	 * @returns {Promise<void>}
	 */
	const checkRadioFavs = async () => {
		if (!device) return;
		checkingRadioFavorites = true;
		hasRadioFavs = await checkRadioFavorites(device);
		checkingRadioFavorites = false;
	};

	/**
	 * Lifecycle function that runs when the component is mounted.
	 * Initializes the component's state by loading media files.
	 *
	 * @async
	 * @returns {void}
	 */
	onMount(async () => {
		if (active_actions[0]?.name === 'MP_Play') {
			showSongList = true;
		}
		if (typeof selectedAction !== 'undefined') {
			//this is valid while arriving from edit mode and different device
			if (!deviceStateList.map((a) => a.id)?.includes(selectedAction)) {
				//if the selected action is not in the list of actions then we need to set it to the first action
				selectedAction = deviceStateList[0]?.id;
				updateActiveAction({
					name: selectedAction,
					value: { id: 0, volume: enableVolumeControl ? volume : undefined },
				});
			}
			//by default on add action select device action this will unlock scene navigation while the user just hits next-next-next
			updateActiveAction({ name: selectedAction, ...action });
			sceneIsValid = true;
		}
		const promises = [loadMediaFiles()];
		if (
			selectedAction === 'Radio_PlayFavourite' ||
			active_actions[0]?.name === 'Radio_PlayFavourite'
		) {
			promises.push(checkRadioFavs());
		}
		await Promise.all(promises);
	});

	let previousDeviceId = deviceId;
	$: if (deviceId && deviceId !== previousDeviceId && device) {
		previousDeviceId = deviceId;
		if (
			selectedAction === 'Radio_PlayFavourite' ||
			active_actions[0]?.name === 'Radio_PlayFavourite'
		) {
			checkRadioFavs();
		} else {
			hasRadioFavs = false;
		}
	}

	const toggleVolumeControl = () => {
		enableVolumeControl = !enableVolumeControl;
		if (!enableVolumeControl) {
			updateActiveAction({
				name: selectedAction,
				value: { id: active_actions[0]?.value?.id || 0, volume: undefined },
			});
		} else {
			updateActiveAction({
				name: selectedAction,
				value: { id: active_actions[0]?.value?.id || 0, volume },
			});
		}
	};
	const a11yProps = {
		ariaLabel: $format('label_adjust_value', { default: 'Adjust value' }),
		ariaLabelValue: null,
	};

	const getTranslatedAlert = (alert: string | boolean | undefined): string | undefined => {
		if (!alert || typeof alert !== 'string') {
			return typeof alert === 'string' ? alert : undefined;
		}
		if (alert === 'No favorite radio stations') {
			return $format('no_favorite_radio_stations_title', {
				default: 'No favorite radio stations',
			});
		}
		if (alert.includes('_')) {
			return $format(alert, {
				default: alert,
			});
		}
		return alert;
	};

	$: translatedAlertHeading = getTranslatedAlert(actions[0]?.alert);

	$: isRadioPlaySelected = selectedAction === 'Radio_PlayFavourite';
	$: shouldShowNoFavoritesAlert = isRadioPlaySelected && !hasRadioFavs && !checkingRadioFavorites;
	$: radioPlayOptionName = $format('scene_action_radio_play_favourite', {
		default: 'Play media from radio',
	});
	$: noFavoritesAlertMessage = $format('alert_radio_play_favourite_no_favorites', {
		default: "To select the option '{option}' you must first add a favorite radio station.",
		values: {
			option: radioPlayOptionName,
		},
	}).replace('{option}', radioPlayOptionName);

	$: if (shouldShowNoFavoritesAlert) {
		sceneIsValid = false;
	} else if (isRadioPlaySelected && hasRadioFavs && selectedAction) {
		sceneIsValid = true;
	}
</script>

<Text element="h2" class="mb-s"
	>{capFirstChar($format('title_select_media_state', { default: 'Select media state' }))}</Text
>

{#if values.length > 0}
	{#if actions[0].alert && translatedAlertHeading}
		<Alert class="mb-s" heading={translatedAlertHeading} type={'info'} />
	{/if}

	{#if shouldShowNoFavoritesAlert}
		<Alert class="mb-s" heading={noFavoritesAlertMessage} type={'warning'} />
	{/if}

	<CardGrid sm={200} md={200} xl={200} noPaddingBottom class="mb-l">
		{#each deviceStateList as card, i}
			<div class="d-flex">
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					class="width-full"
					type="horizontal"
					variant="radio"
					title={card.title}
					icon={card.icon}
					disabled={card.id === 'MP_Play' && !mediaFiles.length}
					selected={card.id === selectedAction}
					on:click={() => {
						onChangeDeviceAction(card.id);
					}}
				/>
			</div>
		{/each}
	</CardGrid>
	<Toggle checked={enableVolumeControl} on:click={toggleVolumeControl} id="toggle-gezzu23"
		>{$format('label_volume')}</Toggle
	>
	<Slider
		id="slider-wkmkd9w"
		{a11yProps}
		min={0}
		max={10}
		step={1}
		title={$format('label_volume')}
		value={volume}
		type="simple"
		unit={''}
		disabled={!enableVolumeControl}
		on:value_change={(e) => onChangeVolume(e)}
	/>
{/if}

{#if showSongList}
	<Container>
		<SelectList
			withSearch={true}
			variant={'radio'}
			title={$format('title_select_song', {
				default: 'Select song to play',
			})}
			list={mediaFiles}
			on:click={(e) => selectSong(e)}
		/>
	</Container>
{/if}
